/**
 * Tabs — built on React Aria Components.
 *
 * Pill-shaped container with a sliding active indicator.
 * Better keyboard navigation and accessibility than Radix tabs.
 *
 * Usage:
 *   <AriaTabs>
 *     <AriaTabList>
 *       <AriaTab id="content">Content</AriaTab>
 *       <AriaTab id="knowledge">Knowledge <span className="text-muted-foreground">(11)</span></AriaTab>
 *       <AriaTab id="history">History</AriaTab>
 *     </AriaTabList>
 *     <AriaTabPanel id="content">...</AriaTabPanel>
 *     <AriaTabPanel id="knowledge">...</AriaTabPanel>
 *     <AriaTabPanel id="history">...</AriaTabPanel>
 *   </AriaTabs>
 */

import {
  Tabs,
  TabList,
  Tab,
  TabPanel,
  type TabsProps,
  type TabListProps,
  type TabProps,
  type TabPanelProps,
} from "react-aria-components";
import type React from "react";
import { useRef, useState, useEffect, useCallback } from "react";
import { cn } from "@/lib/utils";

// ── Tabs Root ─────────────────────────────────────────────────

function AriaTabs({ className, ...props }: TabsProps) {
  return <Tabs className={cn("flex flex-col gap-4", className)} {...props} />;
}

// ── Tab List (pill container with sliding indicator) ──────────

function AriaTabList({
  className,
  children,
  ...props
}: Omit<TabListProps<object>, "children"> & { children: React.ReactNode }) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState<{
    left: number;
    width: number;
  } | null>(null);

  const updateIndicator = useCallback(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const activeTab = wrapper.querySelector(
      '[role="tab"][aria-selected="true"]',
    ) as HTMLElement | null;
    if (!activeTab) return;

    const wrapperRect = wrapper.getBoundingClientRect();
    const tabRect = activeTab.getBoundingClientRect();
    setIndicator({
      left: tabRect.left - wrapperRect.left,
      width: tabRect.width,
    });
  }, []);

  useEffect(() => {
    const frame = requestAnimationFrame(updateIndicator);
    return () => cancelAnimationFrame(frame);
  });

  useEffect(() => {
    const observer = new ResizeObserver(updateIndicator);
    if (wrapperRef.current) observer.observe(wrapperRef.current);
    return () => observer.disconnect();
  }, [updateIndicator]);

  return (
    <div
      ref={wrapperRef}
      className={cn(
        "relative inline-flex max-w-full overflow-x-auto w-fit items-center rounded-full bg-foreground/[0.06] p-1",
        className,
      )}
    >
      {/* Sliding indicator — sibling of TabList, not inside it */}
      {indicator && (
        <div
          className="absolute top-1 h-[calc(100%-8px)] rounded-full bg-foreground transition-all duration-200 ease-out"
          style={{
            left: indicator.left,
            width: indicator.width,
          }}
          aria-hidden
        />
      )}
      <TabList className="relative z-10 flex items-center" {...props}>
        {children}
      </TabList>
    </div>
  );
}

// ── Individual Tab ────────────────────────────────────────────

function AriaTab({ className, ...props }: TabProps) {
  return (
    <Tab
      className={({ isSelected, isHovered, isDisabled, isFocusVisible }) =>
        cn(
          "relative z-10 cursor-pointer select-none rounded-full px-4 py-1.5 text-sm font-medium outline-none transition-colors duration-150",
          isSelected
            ? "text-background [&_*]:!text-background"
            : "text-muted-foreground",
          isHovered && !isSelected && "text-foreground",
          isFocusVisible && "ring-2 ring-ring ring-offset-2",
          isDisabled && "pointer-events-none opacity-50",
          className,
        )
      }
      {...props}
    />
  );
}

// ── Tab Panel ─────────────────────────────────────────────────

function AriaTabPanel({ className, ...props }: TabPanelProps) {
  return (
    <TabPanel
      className={cn(
        "flex-1 outline-none pt-2",
        "data-[focus-visible]:ring-2 data-[focus-visible]:ring-ring data-[focus-visible]:ring-offset-2",
        className,
      )}
      {...props}
    />
  );
}

export { AriaTabs, AriaTabList, AriaTab, AriaTabPanel };
